/**
 * =================================================================
 * HYV Urgency Engine — assets/hyv-urgency.js
 * Version: 1.0.0
 * =================================================================
 *
 * Responsibilities:
 *   1. Read embedded variant/rules JSON from DOM
 *   2. Sync urgency module visibility to selected variant
 *   3. Update dynamic values (inventory count, social count, texts)
 *   4. Handle Impact theme's variant change events
 *   5. Handle product-rerender DOM replacement via MutationObserver
 *   6. Animate module reveals on variant change
 *   7. Respect Shopify editor context
 *
 * NO dependencies. Vanilla ES6+, ~4KB minified.
 * =================================================================
 */

(function () {
  'use strict';

  /* Guard: only initialise once per page load */
  if (window.__hyvUrgencyLoaded) return;
  window.__hyvUrgencyLoaded = true;

  /* ── Constants ─────────────────────────────────────────── */
  const SEL_ROOT       = '.hyv-urgency';
  const SEL_MODULE     = '.hyv-u-module';
  const SEL_VARIANTS   = '[data-hyv-variants]';
  const SEL_RULES      = '[data-hyv-rules]';
  const CLS_HIDDEN     = 'hyv-u-module--hidden';
  const CLS_ENTERING   = 'hyv-u-module--entering';

  /* ── Engine class ──────────────────────────────────────── */
  class UrgencyEngine {

    /**
     * @param {HTMLElement} root — .hyv-urgency container
     */
    constructor (root) {
      this.root          = root;
      this.productId     = root.dataset.productId;
      this.threshold     = parseInt(root.dataset.inventoryThreshold, 10) || 10;
      this.socialCount   = parseInt(root.dataset.socialCount, 10)        || 23;
      this.socialPeriod  = root.dataset.socialPeriod                     || 'today';
      this.globalModules = (root.dataset.globalModules || '')
                             .split(',')
                             .map(s => s.trim())
                             .filter(Boolean);

      this.variantsMap   = this._parseJSON(SEL_VARIANTS) || {};
      this.rulesMap      = this._parseJSON(SEL_RULES)    || {};

      /* Initialise display for the currently-selected variant */
      const startVariant = root.dataset.currentVariant;
      if (startVariant) this.update(parseInt(startVariant, 10));
    }

    /* ── Public ─────────────────────────────────────────── */

    /**
     * Recalculate and update all modules for a given variant ID.
     * Called on every variant change event.
     * @param {number} variantId
     */
    update (variantId) {
      const vid   = String(variantId);
      const vData = this.variantsMap[vid] || null;
      const rules = this.rulesMap[vid]    || null;

      /* Determine active modules (variant rule overrides product default) */
      const modules = (rules && Array.isArray(rules.modules) && rules.modules.length)
        ? rules.modules
        : this.globalModules;

      /* Resolve override values from per-variant rules */
      const socialCount      = (rules && rules.social_count)      || this.socialCount;
      const socialPeriod     = (rules && rules.social_period)     || this.socialPeriod;
      const sellingFastText  = (rules && rules.selling_fast_text) || null;
      const bestsellertText  = (rules && rules.bestseller_text)   || null;
      const limitedText      = (rules && rules.limited_text)      || null;

      /* Inventory state for scarcity */
      let showScarcity = false;
      let inventoryQty = 0;

      if (vData && vData.inventory_management === 'shopify') {
        inventoryQty = vData.inventory_quantity || 0;
        showScarcity = (inventoryQty > 0 && inventoryQty <= this.threshold);
      }

      /* Iterate all module elements and update each */
      this.root.querySelectorAll(SEL_MODULE).forEach(el => {
        const mod = el.dataset.module;

        /* ── Text overrides from variant rules ────────────── */
        if (mod === 'bestseller' && bestsellertText) {
          this._setText(el, '.hyv-u-chip__label', bestsellertText);
        }
        if (mod === 'selling_fast' && sellingFastText) {
          this._setText(el, '.hyv-u-chip__label', sellingFastText);
        }
        if (mod === 'limited' && limitedText) {
          this._setText(el, '.hyv-u-chip__label', limitedText);
        }

        /* ── Social module ────────────────────────────────── */
        if (mod === 'social') {
          this._setText(el, '.hyv-u-social__count',  String(socialCount));
          this._setText(el, '.hyv-u-social__period', '\u00a0bought this ' + socialPeriod);
        }

        /* ── Scarcity module ──────────────────────────────── */
        if (mod === 'scarcity') {
          /* Override show flag — must be in modules AND inventory qualifies */
          const shouldShow = modules.includes('scarcity') && showScarcity;

          if (shouldShow) {
            /* Update count text */
            this._setText(el, '.hyv-u-scarcity__count', String(inventoryQty));

            /* Update progressbar */
            const track = el.querySelector('[role="progressbar"]');
            if (track) track.setAttribute('aria-valuenow', inventoryQty);

            const fill = el.querySelector('.hyv-u-scarcity__fill');
            if (fill) {
              const pct = Math.round(
                Math.min((inventoryQty / this.threshold) * 100, 100)
              ) + '%';
              fill.style.setProperty('--hyv-bar-pct', pct);

              /* Re-trigger bar animation */
              fill.classList.add('hyv-bar--replay');
              void fill.offsetWidth; /* force reflow */
              fill.classList.remove('hyv-bar--replay');
            }
          }

          this._setVisible(el, shouldShow);
          return; /* skip the generic show/hide below */
        }

        /* ── Generic show/hide for all other modules ──────── */
        this._setVisible(el, modules.includes(mod));
      });

      /* Store for reference */
      this.root.dataset.currentVariant = variantId;
    }

    /* ── Private ────────────────────────────────────────── */

    _parseJSON (selector) {
      const el = this.root.querySelector(selector);
      if (!el) return null;
      try { return JSON.parse(el.textContent.trim()); }
      catch (e) {
        console.warn('[HYV Urgency] JSON parse error in', selector, e);
        return null;
      }
    }

    _setText (parent, selector, text) {
      const el = parent.querySelector(selector);
      if (el) el.textContent = text;
    }

    /**
     * Show or hide a module element with an entrance animation on reveal.
     * @param {HTMLElement} el
     * @param {boolean} visible
     */
    _setVisible (el, visible) {
      const wasHidden = el.classList.contains(CLS_HIDDEN);

      if (visible) {
        el.classList.remove(CLS_HIDDEN);

        /* Animate only when transitioning from hidden → visible */
        if (wasHidden) {
          el.classList.remove(CLS_ENTERING);
          void el.offsetWidth; /* force reflow to restart animation */
          el.classList.add(CLS_ENTERING);

          /* Clean up class after animation completes */
          const onEnd = () => {
            el.classList.remove(CLS_ENTERING);
            el.removeEventListener('animationend', onEnd);
          };
          el.addEventListener('animationend', onEnd);
        }
      } else {
        el.classList.add(CLS_HIDDEN);
        el.classList.remove(CLS_ENTERING);
      }
    }
  }

  /* ── Registry ───────────────────────────────────────────── */
  /* WeakMap so GC can collect removed DOM elements */
  const registry = new WeakMap();

  function getEngine (root) {
    if (!registry.has(root)) {
      registry.set(root, new UrgencyEngine(root));
    }
    return registry.get(root);
  }

  function initAll () {
    document.querySelectorAll(SEL_ROOT).forEach(root => getEngine(root));
  }

  /* ── Variant ID extraction ──────────────────────────────── */

  /**
   * Find the currently-selected variant ID from a product form.
   * Impact theme updates a hidden `input[name="id"]` on every selection.
   * @param {HTMLElement} form
   * @returns {number|null}
   */
  function getVariantIdFromForm (form) {
    /* Hidden input — most reliable, Impact always updates this */
    const hidden = form.querySelector('input[name="id"]');
    if (hidden && hidden.value) return parseInt(hidden.value, 10);

    /* Checked radio (block selectors) */
    const radio = form.querySelector('input[type="radio"]:checked');
    if (radio && radio.value && !isNaN(radio.value)) return parseInt(radio.value, 10);

    /* Dropdown */
    const select = form.querySelector('select[name="id"]');
    if (select && select.value) return parseInt(select.value, 10);

    return null;
  }

  /**
   * Find all urgency roots associated with a given form.
   * Walks up to the nearest product-rerender or section boundary.
   * @param {HTMLElement} form
   * @returns {NodeList}
   */
  function urgencyRootsForForm (form) {
    const context = form.closest('[id^="product-info-"]')
                 || form.closest('[data-section-type="main-product"]')
                 || form.closest('.shopify-section')
                 || document;
    return context.querySelectorAll(SEL_ROOT);
  }

  /* ── Event listeners ────────────────────────────────────── */

  /*
   * Primary: listen to any change inside a cart-add form.
   * Impact updates the hidden variant ID input on every variant change,
   * but the `change` event fires on the selector element first.
   * We debounce by one microtask so the hidden input is already updated.
   */
  document.addEventListener('change', function (e) {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    /* Use requestAnimationFrame instead of setTimeout 0 — ensures
       Impact has processed the change and updated the hidden input */
    requestAnimationFrame(() => {
      const variantId = getVariantIdFromForm(form);
      if (!variantId) return;

      urgencyRootsForForm(form).forEach(root => {
        getEngine(root).update(variantId);
      });
    });
  });

  /*
   * Secondary: Impact theme dispatches a custom `variant:changed` event.
   * This catches any variant updates that don't go through a standard
   * form change (e.g., programmatic selection).
   */
  document.addEventListener('variant:changed', function (e) {
    const variant = e.detail?.variant ?? e.detail;
    if (!variant?.id) return;

    document.querySelectorAll(SEL_ROOT).forEach(root => {
      getEngine(root).update(variant.id);
    });
  });

  /*
   * Tertiary: Watch for product-rerender replacing urgency DOM.
   * Impact's <product-rerender> element fetches and replaces inner HTML
   * when the URL-synced variant changes (e.g., browser back/forward,
   * or direct URL manipulation). After replacement the old UrgencyEngine
   * instance is orphaned; the new root needs a fresh engine.
   */
  const observer = new MutationObserver(function (mutations) {
    let needsInit = false;

    for (const m of mutations) {
      if (!m.addedNodes.length) continue;

      for (const node of m.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        if (
          node.matches?.(SEL_ROOT) ||
          node.querySelector?.(SEL_ROOT)
        ) {
          needsInit = true;
          break;
        }
      }
      if (needsInit) break;
    }

    if (needsInit) {
      /* Small delay: let Impact finish hydrating the new DOM */
      requestAnimationFrame(initAll);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  /* ── Shopify theme editor support ───────────────────────── */
  document.addEventListener('shopify:section:load',   initAll);
  document.addEventListener('shopify:section:select', initAll);
  document.addEventListener('shopify:section:deselect', initAll);

  /* ── Init ───────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

})();